// page.js — gemeinsamer Ablauf für Seiten in allen Bereichen: Router-Ergebnis → Seitenmodul rendern.
// Ein Seitenmodul exportiert `render(ctx)`; optional gibt es eine Aufräumfunktion zurück.
import { newPageScope } from './api.js';
import { t } from './i18n.js';
import { html, render } from '../ui/core/h.js';
import { playScene, navMotion, fadeThrough, sharedAxis } from '../ui/motion/motion.js';

let cleanup = null;
let currentMod = null;
let generation = 0; // jede neue Seite zählt hoch; eine überholte asynchrone render() darf nichts mehr anfassen
let lastLoc = null; // { nav, path } der zuletzt gezeigten Seite — bestimmt die Bewegung des nächsten Wechsels

/**
 * Bewegung eines Seitenwechsels (Spec 2026-10-21-motion-design.md): anderes Navigationsziel → Fade Through der Seite;
 * gleiches Ziel, tiefer/flacher (Liste ↔ Unterseite) → Shared Axis Z auf dem jetzt sichtbaren Bereich.
 * Seitenmodule mit eigener Bewegung (ownMotion, z. B. Entdecken: Container-Transform) übernehmen den Wechsel selbst.
 */
export function animateNav(outlet, prev, next, mod) {
  const m = navMotion(prev, next);
  if (!m || !outlet) return null;
  if (m.kind === 'through') return playScene(outlet, fadeThrough(), { spring: 'effects-default' });
  if (mod && mod.ownMotion) return null;
  const panes = outlet.querySelector(':scope > .vc-panes');
  if (!panes) return playScene(outlet, sharedAxis('z', m.dir));
  const detail = panes.querySelector(':scope > .is-detail'), list = panes.querySelector(':scope > .is-list');
  const shown = (el) => !!el && el.getClientRects().length > 0;
  const target = m.dir < 0 && shown(list) && !shown(detail) ? list : detail;
  return playScene(target, sharedAxis('z', m.dir), { spring: 'spatial-default' });
}
export async function mountPage(state, ctx) {
  const mod0 = state.route && state.route.module;
  // Gleiches Seitenmodul mit update(): nur aktualisieren (z. B. Liste → Spot-Detail, Karte bleibt)
  const loc = { nav: (state.route && state.route.nav) || '', path: typeof location !== 'undefined' ? location.pathname : '' };
  const prevLoc = lastLoc;
  lastLoc = loc;
  if (mod0 && mod0 === currentMod && typeof mod0.update === 'function' && !state.error) {
    ctx.shell.setActive(state.route.nav || '');
    if (state.route.title) document.title = state.route.title + ' · ViaCamp';
    // update() schaltet Liste/Detail synchron um; die Bewegung startet sofort, ohne auf Daten zu warten
    const done = mod0.update({ ...ctx, outlet: ctx.shell.outlet, params: state.params, query: state.query, state });
    animateNav(ctx.shell.outlet, prevLoc, loc, mod0);
    return done;
  }
  if (typeof cleanup === 'function') { try { cleanup(); } catch (e) { console.error(e); } }
  cleanup = null;
  currentMod = mod0 || null;
  const my = ++generation;
  const signal = newPageScope();
  const outlet = ctx.shell.outlet;
  if (state.error || !state.route || !state.route.module) {
    render(outlet, html`<div class="vc-empty"><span class="vc-icon" aria-hidden="true">${state.notFound ? 'explore_off' : 'error'}</span>
      <h3>${t(state.notFound ? 'ui.not_found_title' : 'ui.load_error_title')}</h3>
      <p>${t(state.notFound ? 'ui.not_found_text' : 'ui.load_error_text')}</p></div>`);
    return;
  }
  const mod = state.route.module;
  ctx.shell.setActive(state.route.nav || '');
  if (state.route.title) document.title = state.route.title + ' · ViaCamp';
  try {
    const pending = mod.render({ ...ctx, outlet, params: state.params, query: state.query, signal, state });
    animateNav(outlet, prevLoc, loc, mod); // der synchrone Teil von render() hat das Gerüst schon gezeichnet
    const r = await pending;
    if (my !== generation) { if (typeof r === 'function') { try { r(); } catch {} } return; } // überholt: sofort aufräumen
    if (typeof r === 'function') cleanup = r;
  } catch (e) {
    // Überholt (z. B. Weiterleitung ins Onboarding während des Ladens): Fehler der alten Seite nicht über die neue malen.
    if (my !== generation) return;
    console.error('[page]', state.route && state.route.path, e);
    render(outlet, html`<div class="vc-empty"><span class="vc-icon" aria-hidden="true">error</span>
      <h3>${t('ui.load_error_title')}</h3><p>${t('ui.load_error_text')}</p></div>`);
  }
  if (ctx.focusOnNavigate !== false && state.fromNav) outlet.focus({ preventScroll: true });
}
